import { useCallback, useEffect, useState } from 'react';
import {
  getReport,
  downloadReportCsv,
  type ReportRow,
  type ReportTotals,
} from '../api/client';
import { useMachines } from '../hooks/useMachines';

/** YYYY-MM-DD в локальном времени (не UTC — иначе вечером «сегодня» уедет на вчера). */
function day(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function startOfMonth(): string {
  const d = new Date();
  return day(new Date(d.getFullYear(), d.getMonth(), 1));
}

const METHOD_LABEL: Record<string, string> = {
  cash: 'Cash',
  online: 'Card',
  unknown: 'Unknown',
};

/**
 * Отчёт за период — то, что бухгалтерия выгружает и сверяет с кассой.
 *
 * Песочница (стенд 384) исключена по умолчанию: её заказы — не выручка.
 * Возвращённые заказы показываем в таблице, но из итогов вычитаем — деньги
 * у клиента.
 */
export function ReportsPage() {
  const { machines } = useMachines();
  const [from, setFrom] = useState(startOfMonth());
  const [to, setTo] = useState(day(new Date()));
  const [picked, setPicked] = useState<number[]>([]);
  const [sandbox, setSandbox] = useState(false);
  const [rows, setRows] = useState<ReportRow[]>([]);
  const [totals, setTotals] = useState<ReportTotals | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await getReport({ from, to, machineIds: picked, includeSandbox: sandbox });
      setRows(r.rows);
      setTotals(r.totals);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [from, to, picked, sandbox]);

  useEffect(() => { void load(); }, [load]);

  const toggle = (id: number) =>
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div className="card" style={{ padding: 16 }}>
        <h2 style={{ margin: '0 0 12px', fontSize: 18 }}>Report</h2>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ fontSize: 12, color: '#6b7280' }}>
            From<br />
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                   style={{ padding: '6px 8px', fontSize: 13 }} />
          </label>
          <label style={{ fontSize: 12, color: '#6b7280' }}>
            To<br />
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
                   style={{ padding: '6px 8px', fontSize: 13 }} />
          </label>

          <div style={{ fontSize: 12, color: '#6b7280' }}>
            Machines<br />
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
              {machines.map((m) => (
                <label key={m.machineId} style={{ fontSize: 13, display: 'flex', gap: 4, alignItems: 'center' }}>
                  <input type="checkbox" checked={picked.includes(m.machineId)}
                         onChange={() => toggle(m.machineId)} />
                  {m.name || m.machineId}
                  {m.isSandbox && <span style={{ fontSize: 10, color: '#a16207' }}>(sandbox)</span>}
                </label>
              ))}
              {machines.length === 0 && <span style={{ fontSize: 13 }}>—</span>}
            </div>
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
              nothing ticked = all production machines
            </div>
          </div>

          <label style={{ fontSize: 13, display: 'flex', gap: 4, alignItems: 'center' }}>
            <input type="checkbox" checked={sandbox} onChange={(e) => setSandbox(e.target.checked)} />
            include sandbox
          </label>

          <button onClick={() => void load()} disabled={loading} style={{ padding: '6px 14px' }}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
          <button
            onClick={() => void downloadReportCsv({ from, to, machineIds: picked, includeSandbox: sandbox })}
            disabled={loading || rows.length === 0}
            style={{ padding: '6px 14px' }}
          >
            Download CSV
          </button>
        </div>

        {err && <div style={{ color: '#dc2626', fontSize: 13, marginTop: 10 }}>⚠ {err}</div>}
      </div>

      {totals && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 12 }}>
          <Tile label="Revenue" value={`${totals.revenueCzk} Kč`} big />
          <Tile label="Orders" value={String(totals.orders)} />
          <Tile label="Sheets" value={String(totals.sheets)} />
          {Object.entries(totals.byMethod).map(([k, v]) => (
            <Tile key={k} label={METHOD_LABEL[k] ?? k} value={`${v.revenueCzk} Kč`} sub={`${v.orders} orders`} />
          ))}
        </div>
      )}

      {totals && totals.byMachine.length > 1 && (
        <div className="card" style={{ padding: 16 }}>
          <h3 style={{ margin: '0 0 8px', fontSize: 15 }}>By machine</h3>
          {totals.byMachine.map((m) => (
            <div key={String(m.machineId)} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, padding: '4px 0' }}>
              <span>{m.machineName ?? (m.machineId ?? 'no machine')}</span>
              <span style={{ fontWeight: 600 }}>{m.revenueCzk} Kč <span style={{ color: '#9ca3af', fontWeight: 400 }}>· {m.orders}</span></span>
            </div>
          ))}
        </div>
      )}

      <div className="card" style={{ padding: 16, overflowX: 'auto' }}>
        <h3 style={{ margin: '0 0 8px', fontSize: 15 }}>Orders ({rows.length})</h3>
        {rows.length === 0 && !loading && (
          <div style={{ color: '#9ca3af', fontSize: 13 }}>Nothing in this period.</div>
        )}
        {rows.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: '#6b7280' }}>
                <th style={{ padding: 6 }}>When</th>
                <th style={{ padding: 6 }}>Code</th>
                <th style={{ padding: 6 }}>Machine</th>
                <th style={{ padding: 6 }}>Type</th>
                <th style={{ padding: 6 }}>Sheets</th>
                <th style={{ padding: 6, textAlign: 'right' }}>Amount</th>
                <th style={{ padding: 6 }}>Payment</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={(r.code ?? '') + i} style={{ borderTop: '1px solid #f3f4f6', opacity: r.refunded ? 0.5 : 1 }}>
                  <td style={{ padding: 6, whiteSpace: 'nowrap' }}>
                    {r.createdAt.slice(0, 10)} {r.createdAt.slice(11, 16)}
                  </td>
                  <td style={{ padding: 6, fontFamily: 'monospace' }}>{r.code ?? '—'}</td>
                  <td style={{ padding: 6 }}>{r.machineName ?? r.machineId ?? '—'}</td>
                  <td style={{ padding: 6 }}>
                    {r.kind === 'copy' ? 'copy' : 'print'} · {r.color === 'color' ? 'colour' : 'b&w'}
                  </td>
                  <td style={{ padding: 6 }}>{r.sheets ?? '—'}</td>
                  <td style={{ padding: 6, textAlign: 'right', fontWeight: 600 }}>
                    {r.priceCzk} Kč{r.refunded && <span style={{ color: '#dc2626' }}> ↩</span>}
                  </td>
                  <td style={{ padding: 6 }}>{METHOD_LABEL[r.paymentMethod] ?? r.paymentMethod}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Tile({ label, value, sub, big }: { label: string; value: string; sub?: string; big?: boolean }) {
  return (
    <div className="card" style={{ padding: 14 }}>
      <div style={{ fontSize: 12, color: '#6b7280' }}>{label}</div>
      <div style={{ fontSize: big ? 26 : 20, fontWeight: 700, marginTop: 2 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: '#9ca3af' }}>{sub}</div>}
    </div>
  );
}
